import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Templates.css';
import AccessConfigEditor from '../../components/common/AccessConfigEditor';
import { DEFAULT_ACCESS_CONFIG } from '../../utils/stakeholderAccess';

import API_URL from '../../config';

const TYPE_LABELS = {
  project: 'Proje',
  checklist: 'Kontrol Listesi',
  sheet_layout: 'Pafta Düzeni',
  symbol_set: 'Sembol Seti',
  calculation: 'Hesaplama',
  report: 'Rapor',
  stakeholder_access: 'Paydaş-Erişim Şablonu',
};

const DISCIPLINE_LABELS = {
  electrical: 'Elektrik',
  fire: 'Yangın',
  weak: 'Zayıf Akım',
};

const VISIBILITY_LABELS = {
  public: 'Herkese Açık',
  followers: 'Takipçiler',
  link_only: 'Link ile',
  private: 'Özel',
};

const Templates = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const token = localStorage.getItem('projabze_token');

  /* --- Tabs --- */
  const [activeTab, setActiveTab] = useState('discover');

  /* --- Discover state --- */
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [sortBy, setSortBy] = useState('imports');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimerRef = useRef(null);

  /* --- My templates --- */
  const [myTemplates, setMyTemplates] = useState([]);
  const [myLoading, setMyLoading] = useState(false);

  /* --- Imported --- */
  const [importedTemplates, setImportedTemplates] = useState([]);
  const [importedLoading, setImportedLoading] = useState(false);

  /* --- Modals --- */
  const [importModal, setImportModal] = useState(null); // template object or null
  const [importAlias, setImportAlias] = useState('');
  const [importNotify, setImportNotify] = useState(true);
  const [importBusy, setImportBusy] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    type: 'project',
    discipline: 'electrical',
    visibility: 'public',
    tags: '',
  });
  const [createFile, setCreateFile] = useState(null);
  // stakeholder_access tipinde dosya yerine bu config kaydedilir (JSON blob olarak)
  const [createAccessConfig, setCreateAccessConfig] = useState(DEFAULT_ACCESS_CONFIG);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');
  const createFileRef = useRef(null);

  /* --- Delete confirm --- */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  /* ========== Auth check ========== */
  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  /* ========== Fetch: Discover ========== */
  const fetchDiscover = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterDiscipline) params.set('discipline', filterDiscipline);
      if (sortBy) params.set('sort', sortBy);
      if (searchQuery.trim()) params.set('q', searchQuery.trim());

      const res = await fetch(`${API_URL}/api/templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : data.templates || []);
    } catch (err) {
      console.error('Templates fetch error:', err);
    }
    setLoading(false);
  }, [token, filterType, filterDiscipline, sortBy, searchQuery]);

  /* ========== Fetch: My templates ========== */
  const fetchMyTemplates = useCallback(async () => {
    setMyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMyTemplates(Array.isArray(data) ? data : data.templates || []);
    } catch (err) {
      console.error('My templates fetch error:', err);
    }
    setMyLoading(false);
  }, [token]);

  /* ========== Fetch: Imported ========== */
  const fetchImported = useCallback(async () => {
    setImportedLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/imported`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setImportedTemplates(Array.isArray(data) ? data : data.templates || []);
    } catch (err) {
      console.error('Imported fetch error:', err);
    }
    setImportedLoading(false);
  }, [token]);

  /* --- Load data on tab change --- */
  useEffect(() => {
    if (!token) return;
    if (activeTab === 'discover') fetchDiscover();
    else if (activeTab === 'mine') fetchMyTemplates();
    else if (activeTab === 'imported') fetchImported();
  }, [activeTab, token, fetchDiscover, fetchMyTemplates, fetchImported]);

  /* --- Debounced search --- */
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      // fetchDiscover will re-trigger via useEffect on searchQuery change
    }, 350);
  };

  /* ========== Actions ========== */

  /* Like toggle */
  const handleLike = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const updater = (list) =>
        list.map((t) =>
          t.id === templateId
            ? { ...t, isLiked: data.liked, likeCount: (t.likeCount || 0) + (data.liked ? 1 : -1) }
            : t
        );
      setTemplates(updater);
      setMyTemplates(updater);
      setImportedTemplates(updater);
    } catch (err) { /* ignore */ }
  };

  /* Favorite toggle */
  const handleFavorite = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}/favorite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const updater = (list) =>
        list.map((t) =>
          t.id === templateId ? { ...t, isFavorited: data.favorited } : t
        );
      setTemplates(updater);
      setMyTemplates(updater);
      setImportedTemplates(updater);
    } catch (err) { /* ignore */ }
  };

  /* Import */
  const handleImport = async () => {
    if (!importModal || importBusy) return;
    setImportBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/${importModal.id}/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ localAlias: importAlias || undefined, notifyUpdates: importNotify }),
      });
      if (res.ok) {
        const updater = (list) =>
          list.map((t) =>
            t.id === importModal.id
              ? { ...t, isImported: true, importCount: (t.importCount || 0) + 1 }
              : t
          );
        setTemplates(updater);
        setImportModal(null);
        setImportAlias('');
        setImportNotify(true);
      }
    } catch (err) {
      console.error('Import error:', err);
    }
    setImportBusy(false);
  };

  /* Remove import */
  const handleRemoveImport = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}/import`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setImportedTemplates((prev) => prev.filter((t) => t.id !== templateId));
        const updater = (list) =>
          list.map((t) =>
            t.id === templateId
              ? { ...t, isImported: false, importCount: Math.max(0, (t.importCount || 1) - 1) }
              : t
          );
        setTemplates(updater);
      }
    } catch (err) { /* ignore */ }
  };

  /* Create template */
  const handleCreate = async () => {
    if (createBusy) return;
    setCreateError('');
    if (!createForm.title.trim()) { setCreateError('Başlık zorunludur.'); return; }
    const isAccessTpl = createForm.type === 'stakeholder_access';
    if (!isAccessTpl && !createFile) { setCreateError('Dosya zorunludur.'); return; }

    setCreateBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', createForm.title.trim());
      fd.append('description', createForm.description.trim());
      fd.append('type', createForm.type);
      fd.append('discipline', createForm.discipline);
      fd.append('visibility', createForm.visibility);
      fd.append('tags', createForm.tags.trim());
      if (isAccessTpl) {
        // accessConfig'i JSON dosyası olarak yükle
        const blob = new Blob(
          [JSON.stringify({ accessConfig: createAccessConfig }, null, 2)],
          { type: 'application/json' }
        );
        fd.append('file', blob, 'access-config.json');
      } else {
        fd.append('file', createFile);
      }

      const res = await fetch(`${API_URL}/api/templates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setMyTemplates((prev) => [data.template || data, ...prev]);
        setCreateModal(false);
        setCreateForm({ title: '', description: '', type: 'project', discipline: 'electrical', visibility: 'public', tags: '' });
        setCreateFile(null);
        setCreateAccessConfig(DEFAULT_ACCESS_CONFIG);
      } else {
        const err = await res.json().catch(() => ({}));
        setCreateError(err.message || 'Şablon oluşturulamadı.');
      }
    } catch (err) {
      setCreateError('Bir hata oluştu.');
    }
    setCreateBusy(false);
  };

  /* Delete own template */
  const handleDelete = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/templates/${deleteTarget}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMyTemplates((prev) => prev.filter((t) => t.id !== deleteTarget));
        setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget));
      }
    } catch (err) { /* ignore */ }
    setDeleteTarget(null);
    setDeleteBusy(false);
  };

  /* ========== Helpers ========== */
  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  if (!user) return null;

  /* ========== Render helpers ========== */
  const renderCard = (t, showRemove = false, showDelete = false) => (
    <div className="tpl-card" key={t.id}>
      <div className="tpl-card-header">
        <span className={`tpl-badge tpl-badge--${t.type || 'project'}`}>
          {TYPE_LABELS[t.type] || t.type}
        </span>
        {t.discipline && (
          <span className="tpl-discipline">{DISCIPLINE_LABELS[t.discipline] || t.discipline}</span>
        )}
      </div>

      <h3 className="tpl-card-title">{t.title}</h3>

      {t.description && (
        <p className="tpl-card-desc">{t.description}</p>
      )}

      <div className="tpl-card-author">
        <div className="tpl-card-avatar">
          {t.author?.avatar
            ? <img src={API_URL + t.author.avatar} alt="" />
            : (t.author?.firstName?.[0] || '') + (t.author?.lastName?.[0] || '')
          }
        </div>
        <span className="tpl-card-author-name">
          {t.author?.firstName} {t.author?.lastName}
        </span>
      </div>

      {t.tags && t.tags.length > 0 && (
        <div className="tpl-card-tags">
          {(Array.isArray(t.tags) ? t.tags : t.tags.split(',')).slice(0, 4).map((tag, i) => (
            <span className="tpl-tag" key={i}>{typeof tag === 'string' ? tag.trim() : tag}</span>
          ))}
        </div>
      )}

      <div className="tpl-card-stats">
        <span className="tpl-stat">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {t.importCount || 0}
        </span>
        <span className={`tpl-stat tpl-stat--like ${t.isLiked ? 'active' : ''}`} onClick={() => handleLike(t.id)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill={t.isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          {t.likeCount || 0}
        </span>
        <span
          className={`tpl-stat tpl-stat--fav ${t.isFavorited ? 'active' : ''}`}
          onClick={() => handleFavorite(t.id)}
          title={t.isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={t.isFavorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </span>
      </div>

      <div className="tpl-card-actions">
        {!t.isImported && !showRemove && !showDelete && (
          <button className="tpl-btn tpl-btn--import" onClick={() => { setImportModal(t); setImportAlias(t.title); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Import Et
          </button>
        )}
        {t.isImported && !showRemove && (
          <span className="tpl-imported-badge">Imported</span>
        )}
        {showRemove && (
          <button className="tpl-btn tpl-btn--remove" onClick={() => handleRemoveImport(t.id)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Kaldır
          </button>
        )}
        {showDelete && (
          <button className="tpl-btn tpl-btn--delete" onClick={() => setDeleteTarget(t.id)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            Sil
          </button>
        )}
      </div>
    </div>
  );

  const renderEmpty = (msg) => (
    <div className="tpl-empty">
      <div className="tpl-empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
      </div>
      <h3>{msg}</h3>
    </div>
  );

  return (
    <div className={`templates-page ${theme}`}>
      <Topbar user={user} onLogout={handleLogout} />
      <Sidebar activeId="templates" user={user} onLogout={handleLogout} />

      <main className="tpl-main">
        {/* Tabs */}
        <div className="tpl-tabs">
          <button className={`tpl-tab ${activeTab === 'discover' ? 'active' : ''}`} onClick={() => setActiveTab('discover')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            Keşfet
          </button>
          <button className={`tpl-tab ${activeTab === 'mine' ? 'active' : ''}`} onClick={() => setActiveTab('mine')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Şablonlarım
          </button>
          <button className={`tpl-tab ${activeTab === 'imported' ? 'active' : ''}`} onClick={() => setActiveTab('imported')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Import Edilenler
          </button>
        </div>

        {/* ====== DISCOVER TAB ====== */}
        {activeTab === 'discover' && (
          <>
            <div className="tpl-filters">
              <div className="tpl-filter-row">
                <select className="tpl-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="">Tüm Tipler</option>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>

                <select className="tpl-select" value={filterDiscipline} onChange={(e) => setFilterDiscipline(e.target.value)}>
                  <option value="">Tüm Disiplinler</option>
                  {Object.entries(DISCIPLINE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>

                <select className="tpl-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="imports">En Çok Import</option>
                  <option value="likes">En Çok Beğeni</option>
                </select>

                <div className="tpl-search-wrap">
                  <svg className="tpl-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input
                    type="text"
                    className="tpl-search-input"
                    placeholder="Şablon ara..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="tpl-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="tpl-loading">Yükleniyor...</div>
            ) : templates.length > 0 ? (
              <div className="tpl-grid">
                {templates.map((t) => renderCard(t))}
              </div>
            ) : (
              renderEmpty('Henüz şablon bulunamadı')
            )}
          </>
        )}

        {/* ====== MY TEMPLATES TAB ====== */}
        {activeTab === 'mine' && (
          <>
            <div className="tpl-mine-header">
              <h2 className="tpl-section-title">Şablonlarım</h2>
              <button className="tpl-btn tpl-btn--create" onClick={() => setCreateModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Yeni Şablon
              </button>
            </div>

            {myLoading ? (
              <div className="tpl-loading">Yükleniyor...</div>
            ) : myTemplates.length > 0 ? (
              <div className="tpl-grid">
                {myTemplates.map((t) => renderCard(t, false, true))}
              </div>
            ) : (
              renderEmpty('Henüz şablonunuz yok')
            )}
          </>
        )}

        {/* ====== IMPORTED TAB ====== */}
        {activeTab === 'imported' && (
          <>
            <h2 className="tpl-section-title" style={{ marginBottom: '1.25rem' }}>Import Edilenler</h2>

            {importedLoading ? (
              <div className="tpl-loading">Yükleniyor...</div>
            ) : importedTemplates.length > 0 ? (
              <div className="tpl-grid">
                {importedTemplates.map((t) => renderCard(t, true))}
              </div>
            ) : (
              renderEmpty('Henüz import edilmiş şablon yok')
            )}
          </>
        )}
      </main>

      <RightSidebar />

      {/* ====== IMPORT MODAL ====== */}
      {importModal && (
        <div className="tpl-overlay" onClick={() => { if (!importBusy) setImportModal(null); }}>
          <div className="tpl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3>Şablonu Import Et</h3>
              <button className="tpl-modal-close" onClick={() => setImportModal(null)}>&times;</button>
            </div>
            <div className="tpl-modal-body">
              <p className="tpl-modal-info">
                <strong>{importModal.title}</strong> şablonunu kendi koleksiyonunuza ekleyin.
              </p>

              <label className="tpl-label">Yerel İsim (Alias)</label>
              <input
                type="text"
                className="tpl-input"
                value={importAlias}
                onChange={(e) => setImportAlias(e.target.value)}
                placeholder="Şablon için özel isim..."
              />

              <label className="tpl-checkbox-label">
                <input
                  type="checkbox"
                  checked={importNotify}
                  onChange={(e) => setImportNotify(e.target.checked)}
                />
                <span>Güncellemelerden haberdar et</span>
              </label>
            </div>
            <div className="tpl-modal-footer">
              <button className="tpl-btn tpl-btn--cancel" onClick={() => setImportModal(null)} disabled={importBusy}>İptal</button>
              <button className="tpl-btn tpl-btn--confirm" onClick={handleImport} disabled={importBusy}>
                {importBusy ? 'Ekleniyor...' : 'Import Et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== CREATE MODAL ====== */}
      {createModal && (
        <div className="tpl-overlay" onClick={() => { if (!createBusy) { setCreateModal(false); setCreateError(''); } }}>
          <div className="tpl-modal tpl-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3>Yeni Şablon Oluştur</h3>
              <button className="tpl-modal-close" onClick={() => { setCreateModal(false); setCreateError(''); }}>&times;</button>
            </div>
            <div className="tpl-modal-body">
              {createError && <div className="tpl-error">{createError}</div>}

              <label className="tpl-label">Başlık *</label>
              <input
                type="text"
                className="tpl-input"
                value={createForm.title}
                onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Şablon başlığı..."
              />

              <label className="tpl-label">Açıklama</label>
              <textarea
                className="tpl-textarea"
                value={createForm.description}
                onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Şablon hakkında kısa bir açıklama..."
                rows={3}
              />

              <div className="tpl-form-row">
                <div className="tpl-form-group">
                  <label className="tpl-label">Tip</label>
                  <select
                    className="tpl-select"
                    value={createForm.type}
                    onChange={(e) => setCreateForm((p) => ({ ...p, type: e.target.value }))}
                  >
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="tpl-form-group">
                  <label className="tpl-label">Disiplin</label>
                  <select
                    className="tpl-select"
                    value={createForm.discipline}
                    onChange={(e) => setCreateForm((p) => ({ ...p, discipline: e.target.value }))}
                  >
                    {Object.entries(DISCIPLINE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="tpl-form-group">
                  <label className="tpl-label">Görünürlük</label>
                  <select
                    className="tpl-select"
                    value={createForm.visibility}
                    onChange={(e) => setCreateForm((p) => ({ ...p, visibility: e.target.value }))}
                  >
                    {Object.entries(VISIBILITY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="tpl-label">Etiketler (virgül ile ayırın)</label>
              <input
                type="text"
                className="tpl-input"
                value={createForm.tags}
                onChange={(e) => setCreateForm((p) => ({ ...p, tags: e.target.value }))}
                placeholder="elektrik, tesisat, pano..."
              />

              {createForm.type === 'stakeholder_access' ? (
                <>
                  <label className="tpl-label">Erişim Yapılandırması</label>
                  <div className="tpl-access-cfg-wrap">
                    <AccessConfigEditor
                      value={createAccessConfig}
                      onChange={setCreateAccessConfig}
                    />
                  </div>
                </>
              ) : (
                <>
                  <label className="tpl-label">Dosya *</label>
                  <div className="tpl-file-area" onClick={() => createFileRef.current?.click()}>
                    <input
                      type="file"
                      ref={createFileRef}
                      style={{ display: 'none' }}
                      accept=".jbz,.dxf,.json,.zip,.pdf,.xlsx,.xls,.csv"
                      onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                    />
                    {createFile ? (
                      <div className="tpl-file-chosen">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <span>{createFile.name}</span>
                        <button className="tpl-file-remove" onClick={(e) => { e.stopPropagation(); setCreateFile(null); }}>&times;</button>
                      </div>
                    ) : (
                      <div className="tpl-file-placeholder">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        <span>Dosya seçmek için tıklayın</span>
                        <small>.jbz, .dxf, .json, .zip, .pdf, .xlsx</small>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="tpl-modal-footer">
              <button className="tpl-btn tpl-btn--cancel" onClick={() => { setCreateModal(false); setCreateError(''); }} disabled={createBusy}>İptal</button>
              <button className="tpl-btn tpl-btn--confirm" onClick={handleCreate} disabled={createBusy}>
                {createBusy ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== DELETE CONFIRM MODAL ====== */}
      {deleteTarget && (
        <div className="tpl-overlay" onClick={() => { if (!deleteBusy) setDeleteTarget(null); }}>
          <div className="tpl-modal tpl-modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-header">
              <h3>Şablonu Sil</h3>
              <button className="tpl-modal-close" onClick={() => setDeleteTarget(null)}>&times;</button>
            </div>
            <div className="tpl-modal-body">
              <p>Bu şablonu kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            </div>
            <div className="tpl-modal-footer">
              <button className="tpl-btn tpl-btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>İptal</button>
              <button className="tpl-btn tpl-btn--danger" onClick={handleDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
